import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import PublicFooter from '../components/public/PublicFooter';
import SchoolDirectoryCard, { DirectorySchool } from '../components/public/SchoolDirectoryCard';
import { useSubmitEnquiry } from '../hooks/useEnquiries';
import { useSchoolInstruments, useSchoolInfo } from '../hooks/useMeetAndGreet';
import TurnstileWidget from '../components/public/TurnstileWidget';
import { useTurnstileGate, isSpamCheckFailed } from '../hooks/useTurnstileGate';

const TRIAL_TYPE_OPTIONS = [
  'Meet & Greet',
  'Phone Call',
  'Trial Lesson',
  'School Tour',
  'Other',
];

const enquirySchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  phone: z.string().max(30).optional().or(z.literal('')),
  childFirstName: z.string().min(1, "Student's first name is required").max(100),
  instrument: z.string().max(100).optional().or(z.literal('')),
  trialType: z.string().max(100).optional().or(z.literal('')),
  subject: z.string().max(200).optional().or(z.literal('')),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000),
  website: z.string().optional(), // honeypot
});

type EnquiryFormData = z.infer<typeof enquirySchema>;

const API_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

type School = DirectorySchool;

const SchoolsDirectoryPage: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSchools, setFilteredSchools] = useState<School[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enquirySchool, setEnquirySchool] = useState<School | null>(null);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const submitMutation = useSubmitEnquiry();
  const { data: schoolInstruments } = useSchoolInstruments(enquirySchool?.slug || '');
  // #412: the site key comes with the chosen school's public info (same as the enquiry page).
  const { data: enquirySchoolInfo, isLoading: loadingEnquirySchoolInfo } = useSchoolInfo(enquirySchool?.slug || '');
  const turnstile = useTurnstileGate(enquirySchoolInfo?.turnstileSiteKey);
  const [spamCheckFailed, setSpamCheckFailed] = useState(false);

  const {
    control: enquiryControl,
    handleSubmit: handleEnquirySubmit,
    reset: resetEnquiryForm,
    formState: { errors: enquiryErrors, isValid: enquiryIsValid },
  } = useForm<EnquiryFormData>({
    resolver: zodResolver(enquirySchema),
    mode: 'onChange',
    defaultValues: { firstName: '', lastName: '', email: '', phone: '', childFirstName: '', instrument: '', trialType: '', subject: '', message: '', website: '' },
  });

  const handleOpenEnquiry = (school: School) => {
    setEnquirySchool(school);
    setEnquirySubmitted(false);
    setSpamCheckFailed(false);
    // A new opening is a new widget: never resend the last opening's (spent) token.
    turnstile.restart();
    resetEnquiryForm();
  };

  const handleCloseEnquiry = () => {
    setEnquirySchool(null);
    setEnquirySubmitted(false);
    resetEnquiryForm();
  };

  const onEnquirySubmit = async (data: EnquiryFormData) => {
    if (!enquirySchool) return;
    setSpamCheckFailed(false);
    try {
      await submitMutation.mutateAsync({
        schoolSlug: enquirySchool.slug,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || undefined,
        childFirstName: data.childFirstName,
        instrument: data.instrument || undefined,
        trialType: data.trialType || undefined,
        subject: data.subject || undefined,
        message: data.message,
        website: data.website || undefined,
        turnstileToken: turnstile.tokenForSubmit,
      });
      setEnquirySubmitted(true);
    } catch (error) {
      // Other errors are shown by the mutation; a spam-check refusal is explained inline.
      if (isSpamCheckFailed(error)) setSpamCheckFailed(true);
      // A token is single use, and the server consumes it even when it then refuses.
      turnstile.reset();
    }
  };

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const url = `${API_URL}/api/v1/public/schools`;
        const response = await axios.get(url);
        setSchools(response.data);
        setFilteredSchools(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching schools:', error);
        setError(error instanceof Error ? error.message : 'Failed to load schools');
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredSchools(schools);
      return;
    }

    const filtered = schools.filter(
      (school) =>
        school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSchools(filtered);
  }, [searchTerm, schools]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Schools
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Check the browser console for more details.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character Decorations - Left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character Decorations - Right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music Notes Decorations */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile Header - Logo centered */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 120,
            height: 'auto',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          Find Your School
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        {/* Piano Background */}
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Logo - top left */}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        {/* Title overlay */}
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          Find Your School
        </Typography>
      </Box>

      {/* Subtitle */}
      <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          Search for your music school and login to your account
        </Typography>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        {/* Search Bar */}
        <Box sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Search schools by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
        </Box>

        {/* Schools Grid */}
        <Grid container spacing={3}>
          {filteredSchools.map((school) => (
            <Grid item xs={12} md={6} lg={4} key={school.slug}>
              <SchoolDirectoryCard school={school} onEnquire={handleOpenEnquiry} />
            </Grid>
          ))}
        </Grid>

        {filteredSchools.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No schools found matching "{searchTerm}"
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setSearchTerm('')}
              sx={{ mt: 2 }}
            >
              Clear Search
            </Button>
          </Box>
        )}

        {/* Back to Home */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button component={Link} to="/" variant="text">
            ← Back to Home
          </Button>
        </Box>
      </Container>

      <PublicFooter schoolName="Music 'n Me" schoolSlug="music-n-me" />

      {/* Enquiry Dialog */}
      <Dialog
        open={!!enquirySchool}
        onClose={handleCloseEnquiry}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {enquirySubmitted ? 'Enquiry Sent' : `Enquire - ${enquirySchool?.name ?? ''}`}
          <IconButton onClick={handleCloseEnquiry} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {enquirySubmitted ? (
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Thank You!
            </Typography>
            <Typography color="text.secondary">
              Your enquiry has been sent. A member of the team will be in touch shortly.
            </Typography>
          </DialogContent>
        ) : (
          <>
            <DialogContent>
              {submitMutation.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to send enquiry. Please try again.
                </Alert>
              )}

              {/* Honeypot */}
              <Controller
                name="website"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField {...field} sx={{ display: 'none' }} tabIndex={-1} aria-hidden="true" autoComplete="off" />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Controller
                  name="firstName"
                  control={enquiryControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      required
                      sx={{ flex: 1 }}
                      error={!!enquiryErrors.firstName}
                      helperText={enquiryErrors.firstName?.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={enquiryControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      required
                      sx={{ flex: 1 }}
                      error={!!enquiryErrors.lastName}
                      helperText={enquiryErrors.lastName?.message}
                    />
                  )}
                />
              </Box>

              <Controller
                name="email"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    required
                    sx={{ mb: 2 }}
                    error={!!enquiryErrors.email}
                    helperText={enquiryErrors.email?.message}
                  />
                )}
              />

              <Controller
                name="phone"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number (Optional)"
                    fullWidth
                    sx={{ mb: 2 }}
                    error={!!enquiryErrors.phone}
                    helperText={enquiryErrors.phone?.message}
                  />
                )}
              />

              <Controller
                name="childFirstName"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Student's First Name *"
                    fullWidth
                    required
                    placeholder="Student's first name"
                    sx={{ mb: 2 }}
                    error={!!enquiryErrors.childFirstName}
                    helperText={enquiryErrors.childFirstName?.message}
                  />
                )}
              />

              <Controller
                name="instrument"
                control={enquiryControl}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Instrument of Interest (Optional)</InputLabel>
                    <Select
                      {...field}
                      label="Instrument of Interest (Optional)"
                    >
                      <MenuItem value="">
                        <em>Not sure yet</em>
                      </MenuItem>
                      {schoolInstruments?.map((inst: { id: string; name: string }) => (
                        <MenuItem key={inst.id} value={inst.name}>
                          {inst.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="trialType"
                control={enquiryControl}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type of Trial (Optional)</InputLabel>
                    <Select
                      {...field}
                      label="Type of Trial (Optional)"
                    >
                      <MenuItem value="">
                        <em>No preference</em>
                      </MenuItem>
                      {TRIAL_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="subject"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Subject (Optional)"
                    fullWidth
                    sx={{ mb: 2 }}
                    error={!!enquiryErrors.subject}
                    helperText={enquiryErrors.subject?.message}
                  />
                )}
              />

              <Controller
                name="message"
                control={enquiryControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    placeholder="Tell us how we can help you..."
                    error={!!enquiryErrors.message}
                    helperText={enquiryErrors.message?.message}
                  />
                )}
              />

              {/* #412: the spam check (the enquiry route refuses a submission without a valid token). */}
              <Box sx={{ mt: 2 }}>
                <TurnstileWidget
                  ref={turnstile.widgetRef}
                  siteKey={enquirySchoolInfo?.turnstileSiteKey}
                  action="enquiry"
                  onToken={turnstile.onToken}
                  onUnavailable={turnstile.onUnavailable}
                />
              </Box>
              {spamCheckFailed && (
                <Alert severity="warning" sx={{ mt: 1 }} data-testid="spam-check-failed">
                  The spam check didn&apos;t load or complete, so your enquiry wasn&apos;t sent. Turn off any content
                  blocker for this page and try again, or contact {enquirySchool?.name ?? 'the school'} directly.
                </Alert>
              )}
              {enquiryIsValid && turnstile.waiting && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="turnstile-waiting">
                  Checking you&apos;re not a robot…
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseEnquiry} variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!enquiryIsValid || loadingEnquirySchoolInfo || !turnstile.ready || submitMutation.isPending}
                onClick={handleEnquirySubmit(onEnquirySubmit)}
              >
                {submitMutation.isPending ? 'Sending...' : 'Send Enquiry'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SchoolsDirectoryPage;
